import { kebabCase } from 'lodash';
import { DropTargetObjects } from './types';

export const getDropTargetsObjects = (
  dropTargets: DropTargetObjects[],
  dragTarget: DropTargetObjects
) => {
  if (dropTargets.length === 0) {
    throw Error('No drop targets');
  }
  if (!(dropTargets[0].ref.current instanceof HTMLElement)) {
    throw Error('First drop target is not an HTML element');
  }
  const parent = dropTargets[0].ref.current.parentElement;
  if (!isHTMLElement(parent))
    throw Error('Drop targets are not attached to the DOM');
  const parentBox = parent.getBoundingClientRect();
  const availableDimensions = getAvailableDimensions(
    parentBox,
    parent,
    dropTargets[0].ref.current
  );
  const childrenIndexMap = new Map(
    Array.from(parent.children).map((child, index) => [child, index])
  );
  const { ref: dragTragetRef } = dragTarget;
  const dropTargetObjects = dropTargets.map(
    ({ ref, props, setStateWithRefCheck }) => {
      if (!isHTMLElement(ref.current)) {
        throw Error('React component not mounted');
      }
      const index = childrenIndexMap.get(ref.current);
      if (index === undefined) {
        throw Error('Drop target has not expected parent');
      }
      return {
        props,
        ref,
        setStateWithRefCheck,
        box: ref.current.getBoundingClientRect(),
        initialIndex: index,
        currentIndex: index,
        isDragTarget: ref === dragTragetRef,
        translateX: 0,
        translateY: 0,
        relativeX: 0,
        relativeY: 0,
        currentX: 0,
        currentY: 0,
      };
    }
  );
  const dropTargetObjectsSorted = dropTargetObjects.sort(
    ({ initialIndex: index1 }, { initialIndex: index2 }) => index1 - index2
  );
  const {
    box: { x: relativeStartX, y: relativeStartY },
  } = dropTargetObjectsSorted[0];

  dropTargetObjectsSorted.forEach(dropTarget => {
    dropTarget.relativeX = dropTarget.box.x - relativeStartX;
    dropTarget.relativeY = dropTarget.box.y - relativeStartY;
    dropTarget.currentX = dropTarget.relativeX;
    dropTarget.currentY = dropTarget.relativeY;
  });

  return {
    dropTargetsObjects: dropTargetObjectsSorted,
    parentBox,
    availableDimensions,
  };
};

export const isHTMLElement = (node: any): node is HTMLElement =>
  node instanceof HTMLElement;

const getAvailableDimensions = (
  parentBox: DOMRect,
  parent?: HTMLElement,
  firstDropTarget?: HTMLElement
) => {
  if (
    !parentBox ||
    !(parent instanceof HTMLElement) ||
    !(firstDropTarget instanceof HTMLElement)
  ) {
    throw Error('Element are not HTMLElements');
  }
  const {
    paddingTop,
    paddingRight,
    paddingBottom,
    paddingLeft,
    borderTopWidth,
    borderRightWidth,
    borderBottomWidth,
    borderLeftWidth,
  } = getStyle(parent, {
    paddingTop: 0,
    paddingRight: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    borderTopWidth: 0,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    borderLeftWidth: 0,
  });
  const { marginTop, marginRight, marginBottom, marginLeft } = getStyle(
    firstDropTarget,
    {
      marginTop: 0,
      marginRight: 0,
      marginBottom: 0,
      marginLeft: 0,
    }
  );
  const deltaLeft = borderLeftWidth + paddingLeft + marginLeft;
  const deltaTop = borderTopWidth + paddingTop + marginTop;
  const deltaRight = borderRightWidth + paddingRight + marginRight;
  const deltaBottom = borderBottomWidth + paddingBottom + marginBottom;
  const availableWidth = parentBox.width - deltaLeft - deltaRight;
  const availableHeight = parentBox.height - deltaTop - deltaBottom;
  return {
    deltaLeft,
    deltaTop,
    deltaRight,
    deltaBottom,
    availableWidth,
    availableHeight,
  };
};

const getStyle = <PropDict extends Record<string, number>>(
  element: HTMLElement,
  props: PropDict
) => {
  const style = window.getComputedStyle(element);
  if (!style) return props;
  return Object.fromEntries(
    Object.keys(props).map(prop => [
      prop,
      Number.parseInt(style.getPropertyValue(kebabCase(prop)), 10),
    ])
  ) as PropDict;
};
